import { FONT_FAMILY_CONFIG, WIDGET_DEFAULT_FONT } from 'constants/fonts';

import { DEFAULT_HEIGHT_PX, DEFAULT_WIDTH_PX } from './defaultTableData';

/**
 * Retrieves the first font family from a font style string,
 * e.g. 'font-family: Arial, sans-serif;'. -> 'Arial'
 *
 * @param fontString - The font string to extract the font family from.
 * @returns The first font family found in the font string or an empty string if no font family was found.
 */
export const getFirstFontFamilyFromStyle = (fontString: string) => {
  if (!fontString || typeof fontString !== 'string') return '';

  // Match 'font-family: ...' until the first semicolon or end of the string.
  const fontMatch = fontString.match(/font-family:([^;]+)/);
  if (fontMatch && fontMatch[1]) {
    const fonts = fontMatch[1].split(',');

    if (fonts.length > 0) {
      // Remove single and double quotes from the font name.
      return fonts[0].trim().replace(/['"]+/g, '');
    }
  }

  return '';
};

/**
 * Converts HTML content into a html table that matches the table widget.
 * TODO: Copied from E1 and needs to be updated - doesn't capture width/height properly
 *
 * @param content - The HTML content to be converted.
 * @param width - The width of the table.
 * @param height - The height of the table.
 * @returns The formatted table.
 */
export const formatHTMLTable = (content: string) => {
  let width = DEFAULT_WIDTH_PX;
  let height = DEFAULT_HEIGHT_PX;

  const colgroup = content.match(/<colgroup>.*?<\/colgroup>/g)?.[0];
  const topRow = content.match(/<tr.*?>.*?<\/tr>/g)?.[0];
  const numCols = topRow?.match(/<td.*?<\/td>|<th.*?<\/th>/g)?.length;

  // all included fonts from the html
  const fontList: string[] = [];

  if (colgroup && !content.match(/colwidth="\d+"/)) {
    const colwidths = colgroup.match(/width="\d+"/g);
    const widths = colwidths?.map((colwidth) => parseFloat(colwidth.replace(/width="|"/g, '')));
    width = widths?.slice(0, numCols).reduce((prev, cur) => prev + cur) || width;

    const rows = content.match(/<tr.*?>.*?<\/tr>/g);
    const newRows: string[] = [];
    rows?.forEach((row) => {
      const cells = row.match(/<td.*?<\/td>|<th.*?<\/th>/g);
      const newCells: string[] = [];
      cells?.forEach((cell, index) => {
        const cellType = cell.slice(0, 3);
        newCells.push(`${cellType} colwidth="${widths?.[index] || 80}" ${cell.slice(3, cell.length)}`);
      });
      row = row.replace(/<td.*<\/td>|<th.*<\/th>/g, newCells.join(''));
      newRows.push(row);
    });
    const newTableBody = newRows.join('');
    content = content.replace(/<tbody.*?<\/tbody>/, `<tbody>${newTableBody}</tbody>`);
  } else if (content.match(/colwidth="\d+"/)) {
    const colwidths = content.match(/colwidth="\d+"/g);
    const widths = colwidths?.map((colwidth) => parseFloat(colwidth.replace(/colwidth="|"/g, '')));
    width = widths?.slice(0, numCols).reduce((prev, cur) => prev + cur) || width;
  }

  // check all font families from the pasted html and replace fonts that we don't support with the default font
  const fontFamilyRegex = /font-family:.*?;/g;
  const fontFamilyMatches = content.match(fontFamilyRegex);

  fontFamilyMatches?.forEach((fontFamilyMatch) => {
    // take the first font - e.g. Arial not sans-serif from the string 'font-family:Arial,sans-serif'
    const firstFontFamily = getFirstFontFamilyFromStyle(fontFamilyMatch) || WIDGET_DEFAULT_FONT;
    if (!FONT_FAMILY_CONFIG[firstFontFamily as keyof typeof FONT_FAMILY_CONFIG]) {
      // replace the font family with the default font family
      content = content.replace(fontFamilyMatch, `font-family: ${WIDGET_DEFAULT_FONT};`);
    } else {
      content = content.replace(fontFamilyMatch, `font-family: ${firstFontFamily};`);
      fontList.push(firstFontFamily);
    }
  });

  return { content, fontList, width, height };
};
